import * as React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { useRouter } from 'found';
import { StatusCard_company } from './__generated__/StatusCard_company.graphql';
import {
  HAS_ACTIVE_BACKGROUND_CHECK,
  HAS_EXPIRED_BACKGROUND_CHECK
} from 'views/DelegatesPage';
import GaugeCard from '../GaugeCard';
import CertificateCategoriesCard from './CertificateCategoriesCard';
import EmptyStateCard from './EmptyStateCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faUserCheck,
  faFileCertificate
} from '@fortawesome/pro-regular-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'minmax(420px, 1fr) max-content max-content',
    gap: '13px',
    '@media (max-width: 1300px)': {
      gridTemplateColumns: 'minmax(0, 1fr) max-content',
      '& > *:first-child': {
        gridColumn: '1 / -1'
      }
    },
    '@media (max-width: 780px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      '& > *:first-child': {
        gridColumn: 'auto'
      }
    }
  },
  certificatesCard: {},
  topTitle: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  catTitle: {
    fontWeight: 800
  },
  catLabel: {
    color: '#6A728F',
    fontSize: 13
  },
  graphContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    marginTop: 13
  },
  graph: {
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    margin: '10px 0 10px 0'
  },
  certPercentage: {
    fontSize: 14,
    fontWeight: 800
  },
  percentageLabel: {
    color: '#6A728F',
    fontSize: 12,
    textAlign: 'center'
  },
  infoText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  divider: {
    width: '100%',
    height: 1,
    background: '#F6F6F7'
  },
  empty: {
    padding: 20,
    color: '#6a728f',
    fontSize: 14
  }
}));

type Props = {
  company: StatusCard_company;
  onClick?: Function;
  className?: string;
};

function StatusCard({ company, onClick, className }: Props) {
  const classes = useStyles();
  const { router } = useRouter();

  const certificatesStatus = company.certificatesStatus;
  const simulatorCompliance = company.simulatorCompliance;
  const backgroundStatus = company.backgroundCheckStatus;
  const categoryStatuses = certificatesStatus?.secondaryCategoryStatuses ?? [];

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.cards}>
        {categoryStatuses.length > 0 ? (
          <CertificateCategoriesCard
            className={classes.certificatesCard}
            categories={categoryStatuses}
          />
        ) : (
          <EmptyStateCard
            className={classes.certificatesCard}
            icon={faFileCertificate}
            title="Certificates"
            message="No certificate categories tracked yet."
          />
        )}
        {simulatorCompliance.activeUsers > 0 ? (
          <GaugeCard
            percentage={true}
            icon={<FontAwesomeIcon icon={faBookOpen} color="#6A728F" />}
            title="6x6 Training"
            description="Compliance with 6x6 hours"
            data={[
              {
                key: 'Compliant',
                value: simulatorCompliance.compliantUsers,
                colour: '#3e55f6',
                onClick: () =>
                  router.push(`/app/delegates?simulatorCompliant=true`)
              },
              {
                key: 'Non-compliant',
                value: simulatorCompliance.uncompliantUsers,
                colour: '#7b8ff7',
                onClick: () =>
                  router.push(`/app/delegates?simulatorCompliant=false`)
              }
            ]}
          />
        ) : (
          <EmptyStateCard
            icon={faBookOpen}
            title="6x6 Training"
            message="6x6 training compliance not tracked."
          />
        )}
        {backgroundStatus.available > 0 ||
        backgroundStatus.expired > 0 ||
        backgroundStatus.expiringSoon > 0 ||
        backgroundStatus.incomplete > 0 ? (
          <GaugeCard
            percentage={true}
            icon={<FontAwesomeIcon icon={faUserCheck} color="#6A728F" />}
            title="Background Checks"
            description="Status of background checks"
            data={[
              {
                key: 'Valid',
                value: backgroundStatus.available,
                colour: '#3e55f6',
                onClick: () =>
                  router.push(
                    `/app/delegates?filter=${HAS_ACTIVE_BACKGROUND_CHECK}`
                  )
              },
              {
                key: 'Expired',
                value: backgroundStatus.expired + backgroundStatus.incomplete,
                colour: '#7b8ff7',
                onClick: () =>
                  router.push(
                    `/app/delegates?filter=${HAS_EXPIRED_BACKGROUND_CHECK}`
                  )
              }
            ]}
          />
        ) : (
          <EmptyStateCard
            icon={faUserCheck}
            title="Background Checks"
            message="No background check records yet."
          />
        )}
      </div>
    </div>
  );
}

export default createFragmentContainer(StatusCard, {
  company: graphql`
    fragment StatusCard_company on Company {
      certificatesStatus {
        secondaryCategoryStatuses {
          secondaryCategory {
            name
            uuid
          }
          totals {
            available
            expired
            expiringSoon
            incomplete
          }
        }
      }
      simulatorCompliance {
        activeUsers
        compliantUsers
        uncompliantUsers
      }
      documentStatus {
        available
        expired
        expiringSoon
        incomplete
      }
      backgroundCheckStatus {
        available
        expired
        expiringSoon
        incomplete
      }
    }
  `
});
